import { Controller } from "@hotwired/stimulus"

/**
 * Line Chart Controller
 * Chart.js を使用した折れ線グラフを表示
 *
 * Usage:
 * <canvas data-controller="line-chart"
 *         data-line-chart-datasets-value='[{"label": "series1", "data": [{"x": 1704067200000, "y": 100}]}]'>
 * </canvas>
 */
export default class extends Controller {
  static values = {
    datasets: Array
  }

  connect() {
    this.loadChartJs()
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
  }

  async loadChartJs() {
    if (window.Chart) {
      this.initChart()
      return
    }

    if (!window.chartJsPromise) {
      window.chartJsPromise = this.loadScripts()
    }

    try {
      await window.chartJsPromise
      this.initChart()
    } catch (error) {
      console.error("Failed to load Chart.js:", error)
    }
  }

  loadScripts() {
    return new Promise((resolve, reject) => {
      const chartScript = document.createElement("script")
      chartScript.src = "https://cdn.jsdelivr.net/npm/chart.js@4.4.1/dist/chart.umd.min.js"
      chartScript.async = true

      chartScript.onload = () => {
        const adapterScript = document.createElement("script")
        adapterScript.src = "https://cdn.jsdelivr.net/npm/chartjs-adapter-date-fns@3.0.0/dist/chartjs-adapter-date-fns.bundle.min.js"
        adapterScript.async = true
        adapterScript.onload = resolve
        adapterScript.onerror = reject
        document.head.appendChild(adapterScript)
      }

      chartScript.onerror = reject
      document.head.appendChild(chartScript)
    })
  }

  initChart() {
    const colors = [
      "rgb(59, 130, 246)",   // blue
      "rgb(16, 185, 129)",   // green
      "rgb(245, 158, 11)",   // amber
      "rgb(239, 68, 68)",    // red
      "rgb(139, 92, 246)",   // purple
      "rgb(236, 72, 153)",   // pink
      "rgb(20, 184, 166)",   // teal
      "rgb(249, 115, 22)",   // orange
    ]

    const datasets = this.datasetsValue.map((dataset, index) => ({
      label: dataset.label || "(default)",
      data: dataset.data,
      borderColor: colors[index % colors.length],
      backgroundColor: colors[index % colors.length] + "20",
      borderWidth: 1.5,
      pointRadius: 0,
      tension: 0.1,
      fill: false
    }))

    this.chart = new window.Chart(this.element, {
      type: "line",
      data: { datasets: datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: "index",
          intersect: false
        },
        scales: {
          x: {
            type: "time",
            time: {
              unit: "day",
              displayFormats: { day: "MM/dd" }
            },
            title: { display: true, text: "日付" }
          },
          y: {
            title: { display: true, text: "値" },
            beginAtZero: false
          }
        },
        plugins: {
          legend: {
            position: "top"
          },
          tooltip: {
            callbacks: {
              title: function(context) {
                const date = new Date(context[0].parsed.x)
                return date.toLocaleString("ja-JP")
              }
            }
          }
        }
      }
    })
  }
}
